import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { dayLabel } from '../lib/format';

/*
 * A single-series daily column chart, hand-rolled in SVG.
 * Follows the dataviz method: one hue (the accent blue, validated on light),
 * columns ≤ 24px with a 4px rounded data end and a square baseline, a 2px
 * surface gap between neighbours, solid hairline gridlines, the peak labelled
 * directly, a per-column hover and keyboard tooltip, and a table view twin.
 * A day still to come (a null value) keeps its slot on the axis, empty.
 */

export interface BarDatum {
  date: string;
  /** Null for a day that has not come yet: its slot stays empty and takes no hover. */
  value: number | null;
  /** A secondary line for the tooltip, e.g. "4 tasks, 2 sandboxes". */
  note?: string;
}

const M = { top: 22, right: 6, bottom: 26, left: 52 };

function niceStep(max: number, ticks: number): number {
  if (max <= 0) return 1;
  const raw = max / ticks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
}

function columnPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

export function BarChart({
  data,
  label,
  formatValue,
  formatTick,
  height = 220,
  loading = false,
  emptyText = 'Nothing in this range.',
}: {
  data: BarDatum[];
  label: string;
  formatValue: (v: number) => string;
  formatTick: (v: number) => string;
  height?: number;
  loading?: boolean;
  emptyText?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [active, setActive] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const tableId = useId();

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(260, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = data.length;
  const plotW = Math.max(10, width - M.left - M.right);
  const plotH = height - M.top - M.bottom;
  const known = useMemo(() => data.filter((d): d is BarDatum & { value: number } => d.value !== null), [data]);
  const last = known.length - 1;
  const max = Math.max(0, ...known.map((d) => d.value));
  const step = niceStep(max, 4);
  const top = max > 0 ? Math.ceil(max / step) * step : step * 4;
  const isEmpty = known.length > 0 && max === 0 && !loading;
  const ticks = isEmpty ? [0] : Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const band = plotW / Math.max(1, n);
  const gap = band >= 6 ? 2 : 1;
  const barW = Math.max(1, Math.min(24, band - gap));
  const y = (v: number) => M.top + plotH - (v / top) * plotH;
  const peak = useMemo(() => (max > 0 ? data.findIndex((d) => d.value === max) : -1), [data, max]);

  const maxLabels = Math.max(2, Math.floor(plotW / 70));
  const every = Math.max(1, Math.ceil(n / maxLabels));
  const labelled = new Set<number>();
  for (let i = n - 1; i >= 0; i -= every) labelled.add(i);

  useEffect(() => {
    if (active !== null && active > last) setActive(null);
  }, [last, active]);

  const total = known.reduce((s, d) => s + d.value, 0);
  const summary = known.length ? `${label}, ${known.length} days from ${dayLabel(known[0].date)} to ${dayLabel(known[last].date)}. Total ${formatValue(total)}.${peak >= 0 ? ` Highest ${formatValue(max)} on ${dayLabel(data[peak].date)}.` : ''}` : `${label}: no data.`;

  const onPointer = (clientX: number) => {
    const el = wrap.current;
    if (!el) return;
    const x = clientX - el.getBoundingClientRect().left - M.left;
    const i = Math.floor(x / band);
    setActive(i >= 0 && i <= last ? i : null);
  };

  const a = active !== null ? data[active] : null;
  const tipX = active !== null ? M.left + active * band + band / 2 : 0;
  const tipY = a ? Math.min(y(a.value ?? 0), M.top + plotH - 4) : 0;
  const tipLeft = Math.min(Math.max(tipX, 80), width - 80);

  return (
    <div>
      <div
        ref={wrap}
        className={`chart${loading ? ' is-loading' : ''}`}
        role="img"
        aria-label={summary}
        tabIndex={0}
        onPointerMove={(e) => onPointer(e.clientX)}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive((v) => v ?? (last >= 0 ? last : null))}
        onBlur={() => setActive(null)}
        onKeyDown={(e) => {
          if (last < 0) return;
          if (e.key === 'ArrowLeft') setActive((v) => Math.max(0, (v ?? last + 1) - 1));
          else if (e.key === 'ArrowRight') setActive((v) => Math.min(last, (v ?? -1) + 1));
          else if (e.key === 'Home') setActive(0);
          else if (e.key === 'End') setActive(last);
          else if (e.key === 'Escape') setActive(null);
          else return;
          e.preventDefault();
        }}
      >
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
          {ticks.map((t) => (
            <g key={t}>
              {t > 0 ? <line className="chart__grid" x1={M.left} x2={width - M.right} y1={Math.round(y(t)) + 0.5} y2={Math.round(y(t)) + 0.5} /> : null}
              <text className="chart__tick" x={M.left - 10} y={y(t) + 3.5} textAnchor="end">
                {formatTick(t)}
              </text>
            </g>
          ))}
          {active !== null ? <rect className="chart__hit is-active" x={M.left + active * band} y={M.top} width={band} height={plotH} /> : null}
          {data.map((d, i) => {
            if (d.value === null || d.value <= 0) return null;
            const h = Math.max(1.5, (d.value / top) * plotH);
            const x = M.left + i * band + (band - barW) / 2;
            return <path key={d.date} className={`chart__bar${active !== null && active !== i ? ' is-dim' : ''}`} d={columnPath(x, M.top + plotH - h, barW, h, 4)} />;
          })}
          <line className="chart__base" x1={M.left} x2={width - M.right} y1={M.top + plotH + 0.5} y2={M.top + plotH + 0.5} />
          {peak >= 0 && active === null ? (
            <text className="chart__tick" x={Math.min(Math.max(M.left + peak * band + band / 2, M.left + 24), width - 30)} y={y(max) - 7} textAnchor="middle" style={{ fill: 'var(--ink)', fontWeight: 500 }}>
              {formatValue(max)}
            </text>
          ) : null}
          {data.map((d, i) =>
            labelled.has(i) ? (
              <text key={d.date} className="chart__tick" x={Math.min(Math.max(M.left + i * band + band / 2, M.left + 18), width - 22)} y={height - 8} textAnchor="middle">
                {dayLabel(d.date)}
              </text>
            ) : null,
          )}
        </svg>
        {isEmpty ? <p className="chart__empty">{emptyText}</p> : null}
        {a && !isEmpty ? (
          <div className="chart__tip" style={{ left: tipLeft, top: tipY }}>
            <strong>{formatValue(a.value ?? 0)}</strong>
            <span>
              <i className="key" aria-hidden="true" />
              {dayLabel(a.date, true)}
            </span>
            {a.note ? <span>{a.note}</span> : null}
          </div>
        ) : null}
      </div>
      <div className="row" style={{ marginTop: 10, visibility: isEmpty ? 'hidden' : undefined }}>
        <button type="button" className="linkish" aria-expanded={showTable} aria-controls={tableId} onClick={() => setShowTable((v) => !v)}>
          {showTable ? 'Hide table' : 'Show as table'}
        </button>
      </div>
      {showTable ? (
        <div id={tableId} style={{ maxHeight: 280, overflow: 'auto', marginTop: 10, border: '1px solid var(--line)' }}>
          <div className="table-wrap">
            <table className="table" style={{ ['--pad' as string]: '14px' }}>
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col" className="r">
                    {label}
                  </th>
                </tr>
              </thead>
              <tbody>
                {known
                  .slice()
                  .reverse()
                  .map((d) => (
                    <tr key={d.date}>
                      <td className="m-title">{dayLabel(d.date, true)}</td>
                      <td className="r num m-end">{formatValue(d.value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
